import React, { useEffect, useRef, useState } from "react";
import { matchRoster, searchWorkersOnline } from "../punches/roster";
import { useKT } from "../i18n/kt";
import SearchField from "../ui/SearchField";

export type Person = { id: string; code: string; name: string };

/**
 * Find a worker by code or name. Codes are what supervisors know, so they lead. With a roster
 * (kept on the phone) the search is local and works without signal.
 */
const PersonPicker = ({
  value,
  onChange,
  placeholder,
  label,
  roster,
  onAddNew,
  addLabel,
}: {
  value: Person | null;
  onChange: (p: Person | null) => void;
  placeholder: string;
  label: string;
  roster?: Person[];
  /** Offer "+ Add “Ganesh”" under the results, so a new person is added without leaving. */
  onAddNew?: (name: string) => void;
  /** e.g. "as a new weaver". */
  addLabel?: string;
}) => {
  const { t } = useKT();
  const [q, setQ] = useState("");
  const [found, setFound] = useState<Person[]>([]);
  const [searched, setSearched] = useState("");
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (!q.trim()) {
      setFound([]);
      return;
    }
    const local = roster?.length ? matchRoster(roster, q) : [];
    if (local.length) {
      setFound(local);
      setSearched(q.trim());
      return;
    }
    // No roster, or someone added on another phone since it was saved: ask the server.
    setFound([]);
    let live = true;
    timer.current = window.setTimeout(async () => {
      const people = await searchWorkersOnline(q);
      // Typed on since: this answer is for older text.
      if (!live) return;
      setFound(people);
      setSearched(q.trim());
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(timer.current);
    };
  }, [q, roster]);

  if (value) {
    return (
      <div className="kr-chips">
        <span className="kr-choice is-on">
          <span className="kw-code">{value.code}</span> {value.name}
        </span>
        <button type="button" className="kr-choice" onClick={() => onChange(null)}>
          {t("Change")}
        </button>
      </div>
    );
  }
  return (
    <>
      <SearchField value={q} onChange={setQ} placeholder={placeholder} label={label} autoCapitalize="characters" />
      {found.length || (onAddNew && q.trim() && searched === q.trim()) ? (
        <div className="kr-picker-results">
          {found.map((p) => (
            <button key={p.id} type="button" onClick={() => { onChange(p); setQ(""); }}>
              <span className="kw-code">{p.code}</span> {p.name}
            </button>
          ))}
          {!found.length ? <div className="kr-picker-none">{t("No one called “{{name}}” yet", { name: q.trim() })}</div> : null}
          {onAddNew && q.trim() && !/^[A-Za-z]{1,4}[_-]?\d+$/.test(q.trim()) ? (
            <button type="button" className="kr-picker-add" onClick={() => onAddNew(q.trim())}>
              + {t("Add “{{name}}”", { name: q.trim() })} {addLabel ? t(addLabel) : ""}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default PersonPicker;
